/**
 * Speed lines — subtle edge streaks at high speed (Stage 2).
 *
 * A DOM overlay of vertical streak lines on the left and right edges of the
 * 9:16 portrait viewport. Opacity and length scale with forward speed when
 * it exceeds ~70% of speedMax. Pure CSS transforms; zero per-frame allocation
 * (styles mutated in place on pooled elements).
 */

import { JUICE, clamp01 } from "./juice";
import { TUNING } from "../sim/tuning";

const SL = JUICE.speedLines;
const P = TUNING.player;

interface Streak {
  el: HTMLDivElement;
  /** Normalized slot position 0..1 within the container's usable band. */
  topFrac: number;
  side: "left" | "right";
}

/**
 * Streak geometry is expressed in PERCENT OF THE HUD CONTAINER, not `vh`.
 * The HUD root is the letterboxed 9:16 viewport (`min(100vh, 177.78vw)` tall,
 * `overflow: hidden`), so on any window narrower than 9:16 — every portrait
 * phone, and any non-portrait desktop window — `vh` overshoots the container
 * and the lower streaks were clipped away entirely.
 *
 * JUICE.speedLines.minLenVh/maxLenVh are reused with the same numeric values,
 * reinterpreted as percent-of-container (that file is owned elsewhere, hence
 * the retained `Vh` names).
 */
export const MIN_LEN_PCT = SL.minLenVh;
export const MAX_LEN_PCT = SL.maxLenVh;

/**
 * Top offset (percent of container) for a streak slot, given the current
 * streak length in percent. Slots are spread over the band `100 - lenPct`, so
 * `top + lenPct <= 100` always: no streak is ever clipped by the container's
 * `overflow: hidden`, at any window aspect.
 */
export function streakTopPct(topFrac: number, lenPct: number): number {
  return topFrac * (100 - lenPct);
}

/**
 * Speed-line controller. Construct with the HUD root (9:16 viewport).
 * Call `update(forwardSpeed, dt)` each render frame.
 */
export class SpeedLines {
  private readonly streaks: Streak[] = [];
  private opacity = 0;

  constructor(root: HTMLDivElement) {
    const total = SL.streakCount * 2;
    for (let i = 0; i < total; i++) {
      const side: "left" | "right" = i < SL.streakCount ? "left" : "right";
      const idx = i % SL.streakCount;
      const el = document.createElement("div");
      // Slots are spread over the band that keeps a full-length streak inside
      // the container: top = topFrac * (100 - len). With len varying per frame
      // the top is re-evaluated in update(), so no streak is ever clipped.
      const topFrac = idx / SL.streakCount;
      Object.assign(el.style, {
        position: "absolute",
        width: "3px",
        background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.8), transparent)",
        pointerEvents: "none",
        opacity: "0",
        willChange: "opacity, height, top",
      } as Partial<CSSStyleDeclaration>);
      el.style.top = `${streakTopPct(topFrac, MIN_LEN_PCT)}%`;
      el.style.left = side === "left" ? "0" : "";
      el.style.right = side === "right" ? "0" : "";
      el.style.height = `${MIN_LEN_PCT}%`;
      root.appendChild(el);
      this.streaks.push({ el, topFrac, side });
    }
  }

  /**
   * Update streak visibility + length based on current forward speed.
   * @param forwardSpeed  player.forwardSpeed (m/s).
   * @param dt            render-frame delta seconds (for smooth opacity lerp).
   */
  update(forwardSpeed: number, dt: number): void {
    const frac = clamp01((forwardSpeed - P.speedStart) / (P.speedMax - P.speedStart));
    const overThreshold = (frac - SL.thresholdFrac) / (1 - SL.thresholdFrac);
    const target = clamp01(overThreshold) * SL.maxOpacity;

    // Smooth opacity toward target.
    const k = 1 - Math.exp(-6 * dt);
    this.opacity += (target - this.opacity) * k;

    if (this.opacity < 0.01) {
      for (let i = 0; i < this.streaks.length; i++) {
        this.streaks[i].el.style.opacity = "0";
      }
      return;
    }

    const lenFrac = clamp01(overThreshold);
    const lenPct = MIN_LEN_PCT + (MAX_LEN_PCT - MIN_LEN_PCT) * lenFrac;
    const opacityStr = this.opacity.toFixed(3);
    const heightStr = `${lenPct}%`;

    for (let i = 0; i < this.streaks.length; i++) {
      const s = this.streaks[i];
      s.el.style.opacity = opacityStr;
      s.el.style.height = heightStr;
      s.el.style.top = `${streakTopPct(s.topFrac, lenPct)}%`;
    }
  }

  reset(): void {
    this.opacity = 0;
    for (let i = 0; i < this.streaks.length; i++) {
      this.streaks[i].el.style.opacity = "0";
    }
  }

  dispose(): void {
    for (const s of this.streaks) s.el.remove();
    this.streaks.length = 0;
  }
}
